import { BaseRenderer } from './BaseRenderer.js';
import { isDebugMode } from '../utils/debug.js';
import { UI_CONFIG, DEBUG_VISUALIZATION_CONFIG, AI_CONFIG } from '../config.js';

/**
 * Canvas2DRenderer - Traditional 2D canvas rendering
 *
 * DUAL RENDERER SYSTEM:
 * This is one of TWO renderers that users can toggle between (R key).
 * Any visual feature added here MUST also be added to ThreeJSRenderer.
 *
 * See: docs/RENDERER_PARITY.md for detailed parity requirements
 *
 * Rendering Approach:
 * - Direct 2D canvas API calls
 * - Simple and fast
 * - No lighting or shadows
 * - Text rendering built-in
 * - Coordinate system: (0,0) = top-left corner
 *
 * Advantages vs ThreeJS:
 * - Simpler implementation
 * - Faster for 2D games
 * - Built-in text rendering
 * - No WebGL required
 *
 * Debug Features (must match ThreeJS):
 * - ✅ Target lines (red lines from soldier to target)
 * - ✅ Predicted aim points (white X markers)
 * - ✅ Off-screen prediction count (purple "OS:N" text)
 * - ✅ Target IDs (yellow "#5 -> #12" text)
 * - ✅ Respects DEBUG_VISUALIZATION_CONFIG flags
 *
 * PARITY STATUS: REFERENCE IMPLEMENTATION
 * ThreeJS should match all features visible here.
 */
export class Canvas2DRenderer extends BaseRenderer {
  constructor(canvas) {
    super(canvas);
    this.ctx = canvas.getContext('2d');

    // Animation state for debug radius circles
    this.debugAnimationState = 'hidden'; // 'hidden', 'growing', 'visible', 'shrinking'
    this.debugAnimationProgress = 0; // 0 to 1
    this.debugAnimationSpeed = 0.05; // ~20 frames = ~333ms
    this.entityAnimationDelays = new WeakMap(); // Store random delays (0-1) per entity
    this.entityAnimationProgress = new WeakMap(); // Store animation progress (0-1) per entity
  }

  /**
   * Easing function for smooth animation
   * Uses easeOutCubic for deceleration at the end
   * @param {number} t - Progress from 0 to 1
   * @returns {number} - Eased value from 0 to 1
   */
  easeOutCubic(t) {
    return 1 - Math.pow(1 - t, 3);
  }

  /**
   * Get or initialize animation delay and progress for an entity
   * @param {Object} entity - The game entity (soldier or base)
   * @returns {Object} - { delay: 0-1, progress: 0-1 }
   */
  getEntityAnimation(entity) {
    if (!this.entityAnimationDelays.has(entity)) {
      // Random delay between 0 and 0.3 (30% of animation duration)
      this.entityAnimationDelays.set(entity, Math.random() * 0.3);
      this.entityAnimationProgress.set(entity, 0);
    }
    return {
      delay: this.entityAnimationDelays.get(entity),
      progress: this.entityAnimationProgress.get(entity)
    };
  }

  /**
   * Update animation progress for an entity
   * @param {Object} entity - The game entity
   * @param {number} globalProgress - Global animation progress (0-1)
   * @returns {number} - Entity-specific progress with delay and easing applied (0-1)
   */
  updateEntityAnimation(entity, globalProgress) {
    const { delay } = this.getEntityAnimation(entity);

    // Apply delay - entity starts animating when global progress > delay
    let localProgress = Math.max(0, (globalProgress - delay) / (1 - delay));
    localProgress = Math.min(1, localProgress);

    // Apply easing
    const easedProgress = this.easeOutCubic(localProgress);

    // Store for next frame
    this.entityAnimationProgress.set(entity, easedProgress);

    return easedProgress;
  }

  /**
   * Clear the canvas to black background
   */
  clearScene() {
    this.ctx.fillStyle = '#000';
    this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);
  }

  /**
   * Render all game entities
   *
   * Render order (back to front):
   * 1. Bases (background)
   * 2. Corpses (on ground)
   * 3. Resources (metal, nebulas, crystals)
   * 4. Soldiers (main units)
   * 5. Projectiles (foreground)
   *
   * Each entity's render() method handles its own Canvas2D drawing.
   *
   * @param {Soldier[]} soldiers - All alive soldiers
   * @param {Projectile[]} projectiles - All active projectiles
   * @param {Corpse[]} corpses - All active corpses
   * @param {TeamBase[]} bases - All bases (alive or destroyed)
   * @param {Resource[]} resources - All resources (Metal, Nebula, Crystal)
   */
  renderEntities(soldiers, projectiles, corpses = [], bases = [], resources = []) {
    // Render bases behind everything
    for (let base of bases) {
      if (base.alive) {
        base.render(this.ctx);
      }
    }

    // Render corpses next (behind soldiers)
    for (let corpse of corpses) {
      if (corpse.active) {
        corpse.render(this.ctx);
      }
    }

    // Render resources
    for (let res of resources) {
      res.render(this.ctx);
    }

    // Render soldiers
    for (let soldier of soldiers) {
      if (soldier.alive && soldier.health > 0) {
        soldier.render(this.ctx);
      }
    }

    // Render projectiles on top
    for (let projectile of projectiles) {
      projectile.render(this.ctx);
    }
  }

  renderDebugInfo(soldiers, projectiles, bases = []) {
    if (!isDebugMode()) return;

    // Animation state machine for debug radius circles
    const debugMode = isDebugMode();
    if (debugMode && this.debugAnimationState === 'hidden') {
      this.debugAnimationState = 'growing';
      this.debugAnimationProgress = 0;
    } else if (!debugMode && (this.debugAnimationState === 'visible' || this.debugAnimationState === 'growing')) {
      this.debugAnimationState = 'shrinking';
    }

    // Update animation progress
    if (this.debugAnimationState === 'growing') {
      this.debugAnimationProgress += this.debugAnimationSpeed;
      if (this.debugAnimationProgress >= 1) {
        this.debugAnimationProgress = 1;
        this.debugAnimationState = 'visible';
      }
    } else if (this.debugAnimationState === 'shrinking') {
      this.debugAnimationProgress -= this.debugAnimationSpeed;
      if (this.debugAnimationProgress <= 0) {
        this.debugAnimationProgress = 0;
        this.debugAnimationState = 'hidden';
        return; // Don't render anything when hidden
      }
    }

    // Draw targeting lines and debug info
    this.ctx.strokeStyle = '#f00';
    this.ctx.lineWidth = UI_CONFIG.DEBUG_LINE_WIDTH;
    this.ctx.globalAlpha = UI_CONFIG.DEBUG_ALPHA;

    let stats = {
      fighting: 0,
      highLevel: 0,
      maxLevel: 0,
      brokenTargeting: 0,
      projectiles: projectiles.length,
      bases: []
    };

    for (let soldier of soldiers) {
      if (!soldier.alive || soldier.health <= 0) continue;

      if (soldier.level >= UI_CONFIG.HIGH_LEVEL_THRESHOLD) stats.highLevel++;
      if (soldier.level >= UI_CONFIG.MAX_LEVEL_THRESHOLD) stats.maxLevel++;

      if (soldier.state === 'fighting' && !soldier.target) {
        stats.brokenTargeting++;
      }

      // Draw perception radius
      if (DEBUG_VISUALIZATION_CONFIG.showPerceptionRadius && soldier.perceptionRange) {
        // Get per-entity animation progress with random delay and easing
        const entityProgress = this.updateEntityAnimation(soldier, this.debugAnimationProgress);

        this.ctx.strokeStyle = '#0ff'; // Cyan
        this.ctx.lineWidth = 1;
        this.ctx.globalAlpha = 0.3 * entityProgress; // Entity-specific animated opacity
        this.ctx.beginPath();
        this.ctx.arc(soldier.x, soldier.y, soldier.perceptionRange * entityProgress, 0, Math.PI * 2); // Entity-specific animated radius
        this.ctx.stroke();
        this.ctx.globalAlpha = UI_CONFIG.DEBUG_ALPHA;
      }

      // Draw weapon radius
      if (DEBUG_VISUALIZATION_CONFIG.showWeaponRadius && soldier.weaponRange) {
        // Get per-entity animation progress with random delay and easing
        const entityProgress = this.updateEntityAnimation(soldier, this.debugAnimationProgress);

        this.ctx.strokeStyle = '#f80'; // Orange
        this.ctx.lineWidth = 1;
        this.ctx.globalAlpha = 0.3 * entityProgress; // Entity-specific animated opacity
        this.ctx.beginPath();
        this.ctx.arc(soldier.x, soldier.y, soldier.weaponRange * entityProgress, 0, Math.PI * 2); // Entity-specific animated radius
        this.ctx.stroke();
        this.ctx.globalAlpha = UI_CONFIG.DEBUG_ALPHA;
      }

      if (soldier.target && soldier.state === 'fighting') {
        stats.fighting++;

        // Draw line to target
        if (DEBUG_VISUALIZATION_CONFIG.showTargetLines) {
          this.ctx.strokeStyle = '#f00';
          this.ctx.lineWidth = UI_CONFIG.DEBUG_LINE_WIDTH;
          this.ctx.beginPath();
          this.ctx.moveTo(soldier.x, soldier.y);
          this.ctx.lineTo(soldier.target.x, soldier.target.y);
          this.ctx.stroke();
        }

        // Draw predicted aim point
        if (DEBUG_VISUALIZATION_CONFIG.showPredictedPositions &&
            soldier.lastPredictedX !== 0 && soldier.lastPredictedY !== 0) {
          this.ctx.fillStyle = '#fff';
          this.ctx.globalAlpha = UI_CONFIG.PREDICTION_MARKER_ALPHA;
          // Draw X at predicted position
          this.ctx.fillRect(soldier.lastPredictedX - UI_CONFIG.PREDICTION_MARKER_SIZE, soldier.lastPredictedY - 1, UI_CONFIG.PREDICTION_MARKER_WIDTH, UI_CONFIG.PREDICTION_MARKER_HEIGHT);
          this.ctx.fillRect(soldier.lastPredictedX - 1, soldier.lastPredictedY - UI_CONFIG.PREDICTION_MARKER_SIZE, UI_CONFIG.PREDICTION_MARKER_HEIGHT, UI_CONFIG.PREDICTION_MARKER_WIDTH);
          this.ctx.globalAlpha = UI_CONFIG.DEBUG_ALPHA;
        }

        // Draw target info for high-level soldiers
        if (soldier.level >= UI_CONFIG.HIGH_LEVEL_THRESHOLD) {
          this.ctx.fillStyle = '#ff0';
          this.ctx.font = UI_CONFIG.FONT_MEDIUM;
          this.ctx.fillText(`#${soldier.id} -> #${soldier.target.id}`,
                  soldier.x + 15, soldier.y);

          // Show off-screen prediction count if > 0
          if (soldier.offScreenPredictionCount > 0) {
            this.ctx.fillStyle = '#f0f';
            this.ctx.fillText(`OS:${soldier.offScreenPredictionCount}`,
                    soldier.x + 15, soldier.y + 10);
          }
        }
      }
    }

    // Draw base perception and weapon radii
    for (let base of bases) {
      if (!base.alive) continue;

      // Draw perception radius
      if (DEBUG_VISUALIZATION_CONFIG.showPerceptionRadius && base.perceptionRange) {
        // Get per-entity animation progress with random delay and easing
        const entityProgress = this.updateEntityAnimation(base, this.debugAnimationProgress);

        this.ctx.strokeStyle = '#0ff'; // Cyan
        this.ctx.lineWidth = 2;
        this.ctx.globalAlpha = 0.3 * entityProgress; // Entity-specific animated opacity
        this.ctx.beginPath();
        this.ctx.arc(base.x, base.y, base.perceptionRange * entityProgress, 0, Math.PI * 2); // Entity-specific animated radius
        this.ctx.stroke();
        this.ctx.globalAlpha = UI_CONFIG.DEBUG_ALPHA;
      }

      // Draw weapon radius
      if (DEBUG_VISUALIZATION_CONFIG.showWeaponRadius && base.weaponRange) {
        // Get per-entity animation progress with random delay and easing
        const entityProgress = this.updateEntityAnimation(base, this.debugAnimationProgress);

        this.ctx.strokeStyle = '#f80'; // Orange
        this.ctx.lineWidth = 2;
        this.ctx.globalAlpha = 0.3 * entityProgress; // Entity-specific animated opacity
        this.ctx.beginPath();
        this.ctx.arc(base.x, base.y, base.weaponRange * entityProgress, 0, Math.PI * 2); // Entity-specific animated radius
        this.ctx.stroke();
        this.ctx.globalAlpha = UI_CONFIG.DEBUG_ALPHA;
      }
    }

    // Collect base stats
    for (let base of bases) {
      stats.bases.push({
        name: base.team.name,
        health: Math.round(base.health),
        maxHealth: base.maxHealth,
        metal: base.resources.metal
      });
    }

    this.ctx.globalAlpha = 1;
    return stats;
  }

  resize(width, height) {
    super.resize(width, height);
    this.canvas.width = width;
    this.canvas.height = height;
  }

  getRendererType() {
    return 'canvas2d';
  }
}